<template>
  <label
    :class="[
      'checkbox',
      { 'checkbox--disabled': disabled },
      { 'checkbox--border': border },
      { 'checkbox--no-click': noClick },
      `checkbox--size-${size}`,
    ]"
  >
    <input
      ref="inputEl"
      v-bind="$attrs"
      :aria-label="ariaLabel"
      type="checkbox"
      class="checkbox__input"
      :disabled="disabled"
      :checked="checked"
      :tabindex="noClick ? '-1' : '0'"
      @change="handleChange"
    />
    <div v-if="!hideLabel" class="checkbox__label">
      {{ label }}
      <HelpTooltip v-if="description">
        <template #description>{{ description }}</template>
      </HelpTooltip>
    </div>
  </label>
</template>

<script lang="ts">
import { computed, defineComponent, onMounted, ref, watch } from 'vue'
import HelpTooltip from '../HelpTooltip.vue'

export default defineComponent({
  name: 'BaseCheckbox',
  components: { HelpTooltip },
  inheritAttrs: false,
  props: {
    label: {
      type: String,
      required: true,
    },
    description: {
      type: String,
      required: false,
      default: undefined,
    },
    checked: {
      type: Boolean,
      default: false,
    },
    indeterminate: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    hideLabel: {
      type: Boolean,
      required: false,
      default: false,
    },
    border: {
      type: Boolean,
      required: false,
      default: true,
    },
    noClick: {
      type: Boolean,
      required: false,
      default: false,
    },
    size: {
      type: String,
      required: false,
      default: 'medium',
    },
  },
  emits: {
    change: (value: boolean) => typeof value === 'boolean',
  },
  setup(props, { emit }) {
    const handleChange = (event: Event) => {
      emit('change', (event.target as HTMLInputElement).checked)
    }

    // Names the input, not the wrapping label, whose text `hide-label` removes.
    // A caller's own `aria-label` loses to this binding — pass `label` instead.
    const ariaLabel = computed(() =>
      props.hideLabel ? props.label : undefined
    )

    const inputEl = ref<HTMLInputElement | null>(null)

    const syncIndeterminate = () => {
      if (inputEl.value) {
        inputEl.value.indeterminate = props.indeterminate
      }
    }

    onMounted(syncIndeterminate)
    watch(() => props.indeterminate, syncIndeterminate)

    return { handleChange, ariaLabel, inputEl }
  },
})
</script>

<style lang="scss" scoped>
.checkbox {
  display: flex;
  gap: $spacing-4xs;
  padding: 0;
  border: 1px solid transparent;
  color: $color-text;
  cursor: pointer;

  &--border {
    background: $color-canvas;
    border-color: $color-border;
  }

  &--disabled {
    cursor: not-allowed;
    color: $color-text-light;
  }

  &--no-click {
    pointer-events: none;
  }

  &--size-medium {
    &.checkbox--border {
      padding: $spacing-4xs $spacing-2xs $spacing-4xs $spacing-3xs;
      border-radius: $spacing-4xs;
    }
  }

  &--size-large {
    &.checkbox--border {
      padding: $spacing-2xs $spacing-s $spacing-2xs $spacing-xs;
      border-radius: $spacing-3xs;
    }
  }

  &__input {
    accent-color: $color-navigation;
    cursor: inherit;
  }

  &__label {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: $spacing-4xs;
    line-height: $spacing-xs;
    width: 100%;
    color: inherit;
  }
}
</style>
